import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { sharedStyles, createButton } from './shared/sharedStyles.js';

/**
 * QuickEditModal - Modal for editing guest criteria before generating a new report.
 *
 * Allows users to customize guest preferences without changing the base Show data.
 * Shows only the guest/interview fields (not podcast/host details).
 *
 * FIELD FIDELITY: Matches IntakeForm.jsx exactly for consistency.
 */
const QuickEditModal = ({ isOpen, onClose, onSave, initialData = {} }) => {
  const [formData, setFormData] = useState({
    // Guest Preferences (from IntakeForm step 3)
    guestTopics: '',
    guestExpertise: '',
    guestType: [],  // Array, max 3 selections
    guestLocation: '',

    // Interview Details (from IntakeForm step 4)
    interviewFormat: '',
    recordingMethod: '',
    timeline: '',

    // Additional Requirements (from IntakeForm step 5)
    additionalRequirements: ''
  });

  // Populate form with initial data when modal opens
  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        guestTopics: initialData.guestTopics || '',
        guestExpertise: initialData.guestExpertise || '',
        guestType: initialData.guestType || [],
        guestLocation: initialData.guestLocation || '',
        interviewFormat: initialData.interviewFormat || '',
        recordingMethod: initialData.recordingMethod || '',
        timeline: initialData.timeline || '',
        additionalRequirements: initialData.additionalRequirements || ''
      });
    }
  }, [isOpen, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGuestTypeToggle = (value) => {
    setFormData(prev => {
      const currentTypes = prev.guestType;
      const isSelected = currentTypes.includes(value);

      if (isSelected) {
        // Deselect
        return {
          ...prev,
          guestType: currentTypes.filter(t => t !== value)
        };
      } else if (value === 'any') {
        // "No preference" clears all others
        return {
          ...prev,
          guestType: ['any']
        };
      } else if (currentTypes.length < 3) {
        // Add new selection (max 3), removing "any" if present
        const newTypes = currentTypes.filter(t => t !== 'any');
        return {
          ...prev,
          guestType: [...newTypes, value]
        };
      }

      return prev;
    });
  };

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  if (!isOpen) return null;

  // Guest type options - MUST match IntakeForm.jsx line 711-718
  const guestTypeOptions = [
    { value: 'entrepreneurs', label: 'Entrepreneurs' },
    { value: 'executives', label: 'Executives' },
    { value: 'experts', label: 'Industry Experts' },
    { value: 'authors', label: 'Authors' },
    { value: 'researchers', label: 'Researchers' },
    { value: 'creators', label: 'Content Creators' },
    { value: 'any', label: 'No preference' }
  ];

  const formStyles = {
    label: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.8rem',
      fontWeight: '500',
      color: sharedStyles.colors.textSecondary,
      display: 'block',
      marginBottom: '0.5rem'
    },
    input: {
      ...sharedStyles.inputStyles,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.875rem',
      padding: '0.5rem 0.75rem',
      width: '100%'
    },
    textarea: {
      ...sharedStyles.inputStyles,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.875rem',
      padding: '0.5rem 0.75rem',
      resize: 'vertical',
      width: '100%',
      minHeight: '80px'
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={onClose}
      >
        {/* Modal */}
        <div
          style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px',
              borderBottom: `1px solid ${sharedStyles.colors.surfaceAlt}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <h2
              style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: sharedStyles.colors.textPrimary,
                margin: 0
              }}
            >
              Customize Guest Criteria
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: sharedStyles.colors.textSecondary
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Form Content */}
          <div style={{ padding: '20px' }}>
            <p
              style={{
                fontSize: '0.95rem',
                fontWeight: '500',
                color: sharedStyles.colors.textPrimary,
                marginBottom: '20px',
                lineHeight: '1.5'
              }}
            >
              Customize the guest criteria for this report. Leave fields blank to use your show's default settings.
            </p>

            {/* Guest Type Multi-Select - Matching IntakeForm exactly */}
            <div style={{ marginBottom: '16px' }}>
              <label style={formStyles.label}>
                Guest Type
                <span style={{
                  fontSize: '0.75rem',
                  color: sharedStyles.colors.textMuted,
                  marginLeft: '0.5rem'
                }}>
                  (Select up to 3)
                </span>
              </label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.4rem',
                padding: '0.6rem',
                border: `2px solid ${sharedStyles.colors.secondaryLight}`,
                borderRadius: '8px',
                background: sharedStyles.colors.surface,
                minHeight: '3rem'
              }}>
                {guestTypeOptions.map(option => {
                  const isSelected = formData.guestType.includes(option.value);
                  const isDisabled = formData.guestType.length >= 3 && !isSelected && option.value !== 'any';

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleGuestTypeToggle(option.value)}
                      disabled={isDisabled}
                      style={{
                        padding: '0.3rem 0.6rem',
                        background: isSelected
                          ? `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`
                          : 'white',
                        border: `1px solid ${isSelected ? sharedStyles.colors.primary : sharedStyles.colors.secondaryLight}`,
                        borderRadius: '16px',
                        fontSize: '0.75rem',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        color: isSelected
                          ? 'white'
                          : isDisabled
                            ? sharedStyles.colors.textMuted
                            : sharedStyles.colors.textSecondary,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s',
                        opacity: isDisabled ? 0.5 : 1,
                        fontWeight: isSelected ? '500' : 'normal'
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
                {formData.guestType.length >= 3 && !formData.guestType.includes('any') && (
                  <span style={{
                    fontSize: '0.75rem',
                    color: sharedStyles.colors.textMuted,
                    alignSelf: 'center',
                    marginLeft: '0.5rem'
                  }}>
                    Maximum 3 selected
                  </span>
                )}
              </div>
            </div>

            {/* Guest Topics */}
            <div style={{ marginBottom: '16px' }}>
              <label style={formStyles.label}>
                Guest Topics & Expertise
              </label>
              <textarea
                name="guestTopics"
                value={formData.guestTopics}
                onChange={handleChange}
                placeholder="e.g., AI/ML, startup growth, product management..."
                style={formStyles.textarea}
              />
            </div>

            {/* Specific Expertise */}
            <div style={{ marginBottom: '16px' }}>
              <label style={formStyles.label}>
                Specific Expertise or Experience
              </label>
              <textarea
                name="guestExpertise"
                value={formData.guestExpertise}
                onChange={handleChange}
                placeholder="e.g., Built companies to 8-figure ARR, published research..."
                style={formStyles.textarea}
              />
            </div>

            {/* Guest Location */}
            <div style={{ marginBottom: '16px' }}>
              <label style={formStyles.label}>
                Location Preference
              </label>
              <input
                type="text"
                name="guestLocation"
                value={formData.guestLocation}
                onChange={handleChange}
                placeholder="e.g., San Francisco Bay Area, Remote OK, US-based..."
                style={formStyles.input}
              />
            </div>

            {/* Interview Format - Matching IntakeForm options exactly */}
            <div style={{ marginBottom: '16px' }}>
              <label style={formStyles.label}>
                Interview Format
              </label>
              <select
                name="interviewFormat"
                value={formData.interviewFormat}
                onChange={handleChange}
                style={formStyles.input}
              >
                <option value="">Select format</option>
                <option value="conversational">Conversational</option>
                <option value="structured">Structured Q&A</option>
                <option value="storytelling">Storytelling</option>
                <option value="debate">Debate/Discussion</option>
                <option value="educational">Educational</option>
                <option value="mixed">Mixed approach</option>
              </select>
            </div>

            {/* Recording Method - Matching IntakeForm options exactly */}
            <div style={{ marginBottom: '16px' }}>
              <label style={formStyles.label}>
                Recording Method
              </label>
              <select
                name="recordingMethod"
                value={formData.recordingMethod}
                onChange={handleChange}
                style={formStyles.input}
              >
                <option value="">Select method</option>
                <option value="remote">Remote (Zoom, etc.)</option>
                <option value="in-person">In-person</option>
                <option value="phone">Phone</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>

            {/* Timeline - Matching IntakeForm options exactly */}
            <div style={{ marginBottom: '16px' }}>
              <label style={formStyles.label}>
                Booking Timeline
              </label>
              <select
                name="timeline"
                value={formData.timeline}
                onChange={handleChange}
                style={formStyles.input}
              >
                <option value="">Select timeline</option>
                <option value="asap">ASAP</option>
                <option value="1-2-weeks">1-2 weeks</option>
                <option value="1-month">Within a month</option>
                <option value="flexible">Flexible</option>
              </select>
            </div>

            {/* Additional Requirements */}
            <div style={{ marginBottom: '16px' }}>
              <label style={formStyles.label}>
                Additional Requirements
              </label>
              <textarea
                name="additionalRequirements"
                value={formData.additionalRequirements}
                onChange={handleChange}
                placeholder="Any special requirements or preferences..."
                style={formStyles.textarea}
              />
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '20px',
              borderTop: `1px solid ${sharedStyles.colors.surfaceAlt}`,
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}
          >
            <button
              onClick={onClose}
              style={{
                ...createButton('secondary'),
                padding: '10px 20px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                ...createButton('primary'),
                padding: '10px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Save size={18} />
              Save & Generate Report
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuickEditModal;
